<template>
    <div>
        <div class="page-content px-4 top">
            <template-breadcrumb v-if="isDataLoaded" :title="details.name" />
            <template-header v-if="isDataLoaded" :title="details.name" />
            <b-card footer-class="p-0" class="rounded-lg">
                <div class="profile-details">
                    <form>
                        <input type="hidden" name="id" :value="templateId" />
                    </form>

                    <loader-profile
                        v-if="!isDataLoaded"
                        :show-image="false"
                    ></loader-profile>

                    <b-row v-else-if="isDataLoaded">
                        <b-col sm="6">
                            <b-form-group
                                label-for="name"
                                label-cols="3"
                                label="Name:"
                            >
                                <b-input
                                    v-if="isEditMode"
                                    id="name"
                                    v-model="editDetails.name"
                                    :class="{
                                        'border-danger': errors.has('name')
                                    }"
                                    v-validate="'required'"
                                    name="name"
                                    type="text"
                                    placeholder="Name (required)"
                                    :disabled="isSavingEdit"
                                />
                                <div v-else class="col-form-label">
                                    {{ details.name }}
                                </div>
                            </b-form-group>
                            <b-form-group
                                label-for="type"
                                label-cols="3"
                                label="Type:"
                            >
                                <div class="col-form-label">
                                    {{
                                        details.template_type
                                            ? details.template_type.value
                                            : ""
                                    }}
                                </div>
                            </b-form-group>
                            <b-form-group
                                label-for="description"
                                label-cols="3"
                                label="Description:"
                            >
                                <b-textarea
                                    v-if="isEditMode"
                                    v-model="editDetails.description"
                                    :class="{
                                        'border-danger': errors.has(
                                            'description'
                                        )
                                    }"
                                    name="description"
                                    placeholder="Description (optional)"
                                    :disabled="isSavingEdit"
                                />
                                <div
                                    v-else
                                    class="col-form-label"
                                    v-html="
                                        convertNewLineToBreak(
                                            details.description
                                        )
                                    "
                                ></div>
                            </b-form-group>
                        </b-col>
                        <b-col sm="6">
                            <b-form-group
                                label-for="create_at"
                                label-cols="3"
                                label="Created:"
                            >
                                <div class="col-form-label">
                                    {{ _carbonDateDisplay(details.create_at) }}
                                </div>
                            </b-form-group>
                            <b-form-group
                                label-for="last_updated"
                                label-cols="3"
                                label="Updated:"
                            >
                                <div class="col-form-label">
                                    {{ _carbonDateDisplay(details.update_at) }}
                                </div>
                            </b-form-group>
                            <b-form-group
                                label-for="last_used"
                                label-cols="3"
                                label="Last Used:"
                            >
                                <div class="col-form-label">
                                    <span v-if="details && details.last_used">{{
                                        _setDateDisplay(details.last_used)
                                    }}</span>
                                </div>
                            </b-form-group>
                            <b-form-group
                                label-for="total_used"
                                label-cols="3"
                                label="Total Use:"
                            >
                                <div class="col-form-label">
                                    {{ details.total_used }}
                                </div>
                            </b-form-group>
                        </b-col>
                    </b-row>
                </div>

                <template v-slot:footer no-body>
                    <b-tabs card class="list" lazy v-if="templateId">
                        <b-tab>
                            <template v-slot:title>
                                <span>
                                    <i class="cus-page-white-lightning"></i
                                    >Template
                                </span>
                            </template>
                            <b-card-text>
                                <button
                                    v-if="medicalNoteCategories"
                                    @click="saveContent"
                                    :disabled="isSavingEdit"
                                    class="btn btn-secondary mb-2"
                                >
                                    <i class="cus-disk"></i>&nbsp; Save
                                </button>
                                <b-row class="mt-3">
                                    <b-col v-if="isSoapContentLoading" sm="12">
                                        <SoapContentLoading />
                                    </b-col>
                                    <template v-else>
                                        <b-col class="side-content" sm="3">
                                            <template
                                                v-if="medicalNoteCategories"
                                            >
                                                <div
                                                    v-for="node in medicalNoteCategories"
                                                    :key="node.id"
                                                >
                                                    <recursive-section
                                                        :ref="`recursive-section-${node.id}`"
                                                        class="pl-3"
                                                        :node="node"
                                                        :selectedSection="
                                                            selectedSection
                                                        "
                                                        :isMakeParentBold="true"
                                                        module="template"
                                                        @update-checkbox="
                                                            updateCheckbox
                                                        "
                                                    />
                                                </div>
                                            </template>
                                        </b-col>
                                        <b-col sm="9">
                                            <b-card-text>
                                                <SoapContentTemplate
                                                    ref="soapContent"
                                                    :selectedSection="
                                                        selectedSection
                                                    "
                                                    :medicalNoteContentAll="
                                                        medicalNoteContentAll
                                                    "
                                                    :templateId="templateId"
                                                    @update-is-error="
                                                        isError => {
                                                            hasError = isError;
                                                        }
                                                    "
                                                />
                                            </b-card-text>
                                        </b-col>
                                    </template>
                                </b-row>
                            </b-card-text>
                        </b-tab>
                    </b-tabs>
                </template>
            </b-card>
        </div>
    </div>
</template>

<script>
/**
 * @name TemplateMedicalNote
 * @description Profile of template Medical Note
 * @see apps/application/views/staff/business/template/form/view.php
 * @see apps/application/views/staff/business/template/form/navbar.php
 * @see apps/application/controllers/business/template.php
 */

import TemplateHeader from "@/module/settings/template/header.vue";
import TemplateBreadcrumb from "@/module/settings/template/breadcrumb.vue";
import LoaderProfile from "@/components/elements/loader-profile.vue";
import TEMPLATES from "@/constants/templates";
import TEMPLATE_SERVICES from "@/services/templates";
import TemplateSoapServices from "@/services/template/template-soap";
import SettingTemplateMixin from "@/mixins/settings-template";
import RecursiveSection from "@/components/soap/new-section-recursive.vue";
import SoapContentLoading from "@/components/soap/soap-content-loading";
import SoapContentTemplate from "@/components/setting-template/soap/soap-content-template.vue";
import { cloneDeep, reduce, merge } from "lodash";

export default {
    mixins: [SettingTemplateMixin],
    components: {
        TemplateHeader,
        TemplateBreadcrumb,
        LoaderProfile,
        RecursiveSection,
        SoapContentLoading,
        SoapContentTemplate
    },
    data() {
        return {
            templateId: null,
            isDataLoaded: false,
            medicalNoteCategories: {},
            medicalNoteContentAll: {},
            selectedSection: [],
            selectedCategories: [],
            isSoapContentLoading: false,
            hasError: false
        };
    },
    computed: {
        details: {
            get() {
                return this.$store.state.settingsTemplate.details;
            },
            set(value) {
                this.$store.commit("settingsTemplate/setDetails", value);
            }
        },
        editDetails: {
            get() {
                return this.$store.state.settingsTemplate.editDetails;
            },
            set(value) {
                this.$store.commit("settingsTemplate/setEditDetails", value);
            }
        },
        isEditMode: {
            get() {
                return this.$store.state.settingsTemplate.isEditMode;
            }
        },
        isSavingEdit() {
            return this.$store.state.settingsTemplate.isSavingEdit;
        },
        routeParam() {
            return this.$route.params.id;
        }
    },
    watch: {
        routeParam() {
            this.fetchData();
            this.fetchMedicalNoteTemplateCategory();
        }
    },
    mounted() {
        this.fetchData();
        this.fetchMedicalNoteTemplateCategory();

        this.$store.commit(
            "settingsTemplate/setTemplateType",
            TEMPLATES.TYPE.MEDICAL_NOTE
        );
        this.$store.dispatch("references/getUOM");
    },
    beforeDestroy() {
        // clear vuex store
        this.$store.dispatch("settingsTemplate/reset");
    },
    methods: {
        async reloadPageDetails() {
            await this.fetchData();
        },
        async fetchData() {
            try {
                this.isDataLoaded = false;
                let encodedTemplateId = this.routeParam;
                this.templateId = null;

                // fetch details
                let fetchDetails = await this.$http.get(
                    `${process.env.BARRAMUNDI}account/template/${encodedTemplateId}?encoded=true`
                );

                let res = fetchDetails.data.response;
                this.details = res.resources || res.resource;
                this.templateId = this.details.id;

                this.isDataLoaded = true;

                this.validateTemplateType(
                    this.details.type,
                    TEMPLATES.TYPE.MEDICAL_NOTE
                );

                // store templateId in vuex
                this.$store.commit("settingsTemplate/setTemplateId", this.templateId); // prettier-ignore
                this.$store.commit(
                    "settingsTemplate/setModalItem",
                    this.$refs.modalItem
                );
            } catch (error) {
                console.error(error);
                this.isDataLoaded = true;
                this._message(error.message, "danger");
            }
        },
        async fetchMedicalNoteTemplateCategory() {
            try {
                this.isSoapContentLoading = true;
                let encodedTemplateId = this.routeParam;

                let res = await TemplateSoapServices.getMedicalNoteTemplateCategory(
                    encodedTemplateId
                );

                if (res && res.code == 200) {
                    if (res.resource) {
                        this.medicalNoteCategories = cloneDeep(
                            res.resource.category
                        );

                        if (this.medicalNoteCategories) {
                            this.selectedSection = this.getSelectedCategories(
                                this.medicalNoteCategories
                            );
                        }

                        if (res.resource.content) {
                            let medicalNoteContentAll = Object.values(
                                res.resource.content
                            );

                            this.medicalNoteContentAll = this.normalizeDataStructure(
                                medicalNoteContentAll
                            );
                        }
                    } else {
                        throw new Error(
                            "Failed to get template medical note categories"
                        );
                    }
                } else {
                    throw new Error("Failed to get template variables list");
                }
            } catch (error) {
                this._message(error.message, "danger");
            } finally {
                this.isSoapContentLoading = false;
                await this.$nextTick();
                if (this.$refs.soapContent) {
                    this.$refs.soapContent.filterMedicalNoteContent();
                }
            }
        },
        async saveDetails() {
            let self = this;

            this.$store.commit("settingsTemplate/toggleSavingEdit", true);

            try {
                let isValid = await this.$validator.validateAll();

                if (!isValid) {
                    this.errors
                        .all()
                        .forEach(error => self._message(error, "danger"));
                    this.$store.commit(
                        "settingsTemplate/toggleSavingEdit",
                        false
                    );
                    return;
                }

                const payload = {
                    id: this.templateId,
                    type: this.editDetails.template_type.id,
                    name: this.editDetails.name,
                    description: this.editDetails.description
                };

                const res = await TEMPLATE_SERVICES.putUpdateGeneric(
                    this.templateId,
                    payload
                );

                if (res?.data?.response?.resource) {
                    // Update the details
                    merge(this.details, res.data.response.resource);
                }

                this.$store.commit("settingsTemplate/commitEditDetails");
                this._message("Record saved");
            } catch (error) {
                console.error(error);
                this._message("Error saving data", "danger");
            }

            this.$store.commit("settingsTemplate/toggleSavingEdit", false);
        },
        async saveContent() {
            if (this.hasError) {
                return;
            }
            this.updateMedicalNoteProperties();

            const updatedContent = this.$refs["soapContent"].medicalNoteContent;
            const cb = reduce(
                this.selectedSection,
                (result, selected) => {
                    result[selected.id] = "on";
                    return result;
                },
                {}
            );

            const extractKeysValue = (result, selected) => {
                const selectedValue = Array.isArray(selected.value)
                    ? selected.value[0]
                    : selected.value;

                switch (selected.fieldType) {
                    case "value_input":
                    case "dropwdown_input":
                    case "custom_field_single_select_input":
                        result[selected.valueId] = selectedValue;
                        break;
                    case "value_dropdown_input":
                        result[selected.valueId] = selectedValue;
                        result[selected.valueTypeId] = selected.value_type;
                        break;
                    case "value_dropdown_input2":
                        result[selected.valueId] = selectedValue;
                        result[selected.value2Id] = selected.value2;
                        break;
                    case "value_dropdown_dropdown_input":
                        result[selected.valueId] = selectedValue;
                        result[selected.value2Id] = selected.value2;
                        result[selected.valueTypeId] = selected.value_type;
                        break;
                    case "custom_field_multi_select_input":
                        result[selected.valueId] = selected.value; // selected value in array
                        break;
                    default:
                        // for "textarea", "input", "datepicker"
                        // Only use data.comment
                        break;
                }

                if (selected.comment !== undefined) {
                    result[selected.id] = selected.comment;
                }
                if (
                    selected.data &&
                    Array.isArray(selected.data) &&
                    selected.data.length > 0
                ) {
                    const childResult = reduce(
                        selected.data,
                        extractKeysValue,
                        result
                    );
                    result = { ...result, ...childResult };
                }
                return result;
            };
            const params = reduce(updatedContent, extractKeysValue, {});
            params.cb = cb;

            const errorMessage = "Error: Medical Note template unable to save.";
            TemplateSoapServices.saveMedicalNoteTemplate(
                this.templateId,
                params
            )
                .then(res => {
                    if (res) {
                        this._message("Changes saved");
                    } else {
                        throw new Error(errorMessage);
                    }
                })
                .catch(e => {
                    this._message(errorMessage);
                    console.error(e);
                });
        },
        async updateCheckbox(node) {
            if (!node) {
                return;
            }

            if (node.isChecked) {
                // Convert selectedSection to a Map for quick lookup
                // Map is faster for lookup
                const sectionMap = new Map(
                    this.selectedSection.map(item => [item.id, item])
                );
                node.elements.forEach(el => sectionMap.set(el.id, el));

                this.selectedSection = Array.from(sectionMap.values());
            } else {
                // Remove elements from the selectedSection
                const nodeElementIds = new Set(node.elements.map(el => el.id));
                this.selectedSection = this.selectedSection.filter(
                    item => !nodeElementIds.has(item.id)
                );
            }

            this.$refs[`recursive-section-${node.parentId}`][0].updateInputKey([
                ...this.selectedSection
            ]);

            await this.$nextTick();
            this.updateMedicalNoteProperties();
            await this.$nextTick();
            this.$refs.soapContent.filterMedicalNoteContent();
        },
        /**
         *
         * To Update the properties in medicalNoteContentAll when changing the checkbox
         * In our input elements, the DOM using medicalNoteContent, so whenever the input field been updated(the property comment, value, value2 and value_type), the medicalNoteContentAll still unchanged
         * So when update the checkbox to show/hide the elements, the value been reset as medicalNoteContent re-copied from medicalNoteContentAll
         * @param updatedValues
         */
        updateMedicalNoteProperties() {
            const updatedMedicalNoteContent = this.$refs["soapContent"]
                .medicalNoteContent;

            const updating = (items, updatedContent) => {
                if (!items) {
                    return items;
                }
                return items.map(item => {
                    const updatedItem = updatedContent.find(
                        u => u.id === item.id
                    );

                    if (updatedItem) {
                        return {
                            ...item,
                            ...Object.fromEntries(
                                Object.entries(updatedItem).filter(
                                    ([key]) => key !== "data"
                                )
                            ),
                            data: Array.isArray(item.data)
                                ? updating(item.data, updatedItem.data)
                                : item.data
                        };
                    }

                    return item;
                });
            };

            this.medicalNoteContentAll = updating(
                this.medicalNoteContentAll,
                updatedMedicalNoteContent
            );
        },
        getSelectedCategories(categories) {
            const selectedCategories = [];
            const category = Object.values(categories);

            while (category.length > 0) {
                const node = category.shift(); // Get the first category

                if (node.isactive !== "1") {
                    continue;
                }

                if (node.isactive === "1") {
                    selectedCategories.push({
                        id: node.id,
                        isactive: node.isactive,
                        parentIndex: node.parentIndex
                    });
                }

                // Add child components to the category array
                if (node.components) {
                    category.push(...Object.values(node.components));
                }
            }

            return selectedCategories;
        },
        normalizeDataStructure(obj) {
            if (obj && typeof obj === "object") {
                for (const key in obj) {
                    if (
                        obj[key] &&
                        typeof obj[key].data === "object" &&
                        !Array.isArray(obj[key].data)
                    ) {
                        obj[key].data = Object.values(obj[key].data);
                    }
                    this.normalizeDataStructure(obj[key].data); // Recursively process nested structures
                }
            }
            return obj;
        }
    }
};
</script>

<style scoped>
.top {
    margin-top: 10px;
    margin-bottom: 3px;
}
</style>
